import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../services/api';
import { useAuth } from '../App';
import { datumUndZeit } from '../lib/zeit';
import './UserPaymentMethods.css';

// ZAHLART-SPERRE-KUNDE-001 (Owner 16.09.2026, E2/E3): Zahlarten je Kunde schalten.
// Jede Zahlart hat drei Zustände. „Automatisch" ist der Standard (offene Gebühr sperrt nur Bar);
// „Erlaubt"/„Gesperrt" ist eine ausdrückliche Admin-Wahl und gewinnt immer. Der Server entscheidet
// und protokolliert — diese Fläche zeigt nur an und schickt die Wahl.
export const MODE_LABELS = { auto: 'Automatisch', allowed: 'Erlaubt', blocked: 'Gesperrt' };
const MODES = ['auto', 'allowed', 'blocked'];

export function effectiveLabel(entry) {
  if (entry.allowed) return entry.mode === 'allowed' ? 'erlaubt (Admin)' : 'erlaubt';
  if (entry.reason === 'open_fees') return 'gesperrt — offene Gebühr';
  if (entry.reason === 'cash_cancellations') return 'gesperrt — wiederholte Stornos';
  return 'gesperrt (Admin)';
}

export default function UserPaymentMethods({ userId }) {
  const { can } = useAuth();
  const allowedToManage = Boolean(can && can('finance.manage'));
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (!allowedToManage) return undefined;
    let current = true;
    setError('');
    api.get(`/admin/users/${encodeURIComponent(userId)}/payment-methods`)
      .then((r) => { if (current) setData(r.data); })
      .catch((err) => {
        if (!current) return;
        setError(err?.response?.status === 403
          ? 'Dafür fehlt die Finanz-Berechtigung (finance.manage).'
          : 'Zahlarten konnten nicht geladen werden.');
      });
    return () => { current = false; };
  }, [userId, allowedToManage]);

  if (!allowedToManage) return null;
  if (error) return <section className="upm-card" aria-label="Zahlarten"><p className="upm-error">{error}</p></section>;
  if (!data) return <section className="upm-card" aria-label="Zahlarten"><p className="upm-muted">Zahlarten werden geladen …</p></section>;

  const change = async (method, mode) => {
    if (busy) return;
    setBusy(method);
    try {
      const r = await api.put(`/admin/users/${encodeURIComponent(userId)}/payment-methods`, {
        method, mode, ...(note.trim() ? { note: note.trim() } : {}),
      });
      setData(r.data);
      setNote('');
      if (r.data?.changed) toast.success(`${r.data.methods.find((m) => m.method === method)?.label}: ${MODE_LABELS[mode]}`);
      for (const warning of r.data?.warnings || []) toast.error(warning.message);
    } catch (requestError) {
      toast.error(requestError?.response?.data?.message || 'Zahlart konnte nicht geändert werden');
    } finally {
      setBusy('');
    }
  };

  const openFees = data.open_fees || {};
  const cashCancellations = data.cash_cancellations || {};
  return (
    <section className="upm-card" aria-label="Zahlarten">
      <header>
        <span className="upm-overline">Zahlarten dieses Kunden</span>
        {openFees.count > 0 && (
          <small className="upm-fees">
            {openFees.count} offene Gebühr{openFees.count > 1 ? 'en' : ''} · {(openFees.cents / 100).toFixed(2).replace('.', ',')} €
          </small>
        )}
        {cashCancellations.count > 0 && (
          <small className="upm-fees">
            {cashCancellations.count} Bar-Storno{cashCancellations.count > 1 ? 's' : ''} in {cashCancellations.window_days} Tagen
            {cashCancellations.blocked ? ` · sperrt ab ${cashCancellations.threshold}` : ''}
          </small>
        )}
      </header>
      <p className="upm-hint">
        „Automatisch": offene Gebühr sperrt nur Bar — ebenso drei Bar-Stornos des Kunden in sieben Tagen.
        „Erlaubt"/„Gesperrt" gilt immer — auch gegen die Automatik.
      </p>
      {(data.warnings || []).length > 0 && (
        <ul className="upm-warnings" role="alert">
          {data.warnings.map((w) => <li key={w.code}>{w.message}</li>)}
        </ul>
      )}
      <div className="upm-rows">
        {data.methods.map((entry) => (
          <div className="upm-row" key={entry.method}>
            <div className="upm-row-label">
              <strong>{entry.label}</strong>
              <small className={entry.allowed ? 'is-allowed' : 'is-blocked'}>{effectiveLabel(entry)}</small>
            </div>
            <div className="upm-segments" role="radiogroup" aria-label={`${entry.label} Zustand`}>
              {MODES.map((mode) => (
                <button
                  key={mode}
                  type="button"
                  role="radio"
                  aria-checked={entry.mode === mode}
                  className={entry.mode === mode ? 'is-active' : ''}
                  disabled={Boolean(busy)}
                  onClick={() => change(entry.method, mode)}
                >
                  {MODE_LABELS[mode]}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      <input
        className="upm-note"
        value={note}
        onChange={(event) => setNote(event.target.value)}
        placeholder="Notiz zur nächsten Änderung (optional), z. B. Rückbuchung"
        aria-label="Notiz zur Zahlart-Änderung"
        maxLength={300}
      />
      {(data.audit || []).length > 0 && (
        <details className="upm-audit">
          <summary>Protokoll ({data.audit.length})</summary>
          <ul>
            {data.audit.map((a) => (
              <li key={a.id}>
                <span>{datumUndZeit(a.created_at)}</span>
                <span>{a.actor_name || a.actor_id}</span>
                <span>
                  {data.methods.find((m) => m.method === a.method)?.label || a.method}: {MODE_LABELS[a.old_mode]} → {MODE_LABELS[a.new_mode]}
                  {a.open_fee_count > 0 ? ` (bei ${a.open_fee_count} offener Gebühr)` : ''}
                </span>
                {a.note && <small>{a.note}</small>}
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}
